import React, { useState } from 'react';
import { Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import { Save } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../../app/store';
import { useCreateRegistrationMutation, useUpdateRegistrationMutation } from '../../../../services/RegistrationService';

interface SaveRegistrationButtonProps {
  mode?: 'create' | 'update';
}

const SaveRegistrationButton: React.FC<SaveRegistrationButtonProps> = ({ mode = 'create' }) => {
  const registration = useAppSelector(state => state.registration.currentRegistration);
  const navigate = useNavigate();
  const [createRegistration, { isLoading: isCreating }] = useCreateRegistrationMutation();
  const [updateRegistration, { isLoading: isUpdating }] = useUpdateRegistrationMutation();
  
  const isLoading = isCreating || isUpdating;
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleSave = async () => {
    if (!registration) {
      setSnackbar({
        open: true,
        message: 'No registration data to save',
        severity: 'error'
      });
      return;
    }

    try {
      let result;
      
      if (mode === 'create') {
        result = await createRegistration({
          RegistrationView: registration
        }).unwrap();
        
        setSnackbar({
          open: true,
          message: 'Registration created successfully!',
          severity: 'success'
        });

        // Extract identifiers from the result for redirect
        const { company, center, mrn } = result.identifiers || {};
        
        // Redirect to the Registration view page
        if (company && center && mrn) {
          const redirectUrl = `/Registration?company=${company}&center=${center}&mrn=${mrn}`;
          navigate(redirectUrl);
        }
      } else {
        result = await updateRegistration({
          registrationView: registration
        }).unwrap();
        
        setSnackbar({
          open: true,
          message: 'Registration updated successfully!',
          severity: 'success'
        });

        // Extract identifiers from the registration for redirect
        const { company, center, mrn } = registration.identifiers || {};
        
        // Redirect to the Registration view page
        if (company && center && mrn) {
          const redirectUrl = `/Registration?company=${company}&center=${center}&mrn=${mrn}`;
          navigate(redirectUrl);
        }
      }
    } catch (error) {
      console.error('Failed to save registration:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save registration. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <Button
        variant="contained"
        size="medium"
        onClick={handleSave}
        disabled={isLoading || !registration}
        startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Save />}
        sx={{
          px: 3,
          py: 1,
          fontWeight: 600,
          borderRadius: 1,
          backgroundColor: '#162346',
          fontSize: '0.9rem',
          '&:hover': {
            backgroundColor: '#1a2a52',
          },
          '&:disabled': {
            backgroundColor: '#94a3b8',
          },
        }}
      >
        {isLoading ? (mode === 'create' ? 'Creating...' : 'Updating...') : (mode === 'create' ? 'Create' : 'Update')}
      </Button>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SaveRegistrationButton;
